'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import {
  Database,
  Zap,
  Building2,
  Network,
  ShieldAlert,
  ChevronRight,
  Globe,
  ArrowUpRight,
  Sparkles,
  GitBranch,
  Cpu,
  Layers,
  Lock,
} from 'lucide-react';

interface ServiceItem {
  icon: React.ReactNode;
  title: string;
  bullets: string[];
}

const services: ServiceItem[] = [
  {
    icon: <Database size={24} />,
    title: 'Investment Facilitation & Structuring',
    bullets: [
      'Data centre investment opportunities in high-growth global hubs',
      'Joint Ventures (JV) and tailored Special Purpose Vehicle (SPV) structuring',
      'Equity partnerships and institutional capital alignment strategies',
    ],
  },
  {
    icon: <Zap size={24} />,
    title: 'Land, Power & Connectivity Enablement',
    bullets: [
      'Strategic land parcel identification and acquisition matching',
      'High-capacity power allocation facilitation (MW-level utility alignment)',
      'Dark fiber, high-bandwidth telecom, and redundant carrier connectivity enablement',
    ],
  },
  {
    icon: <Building2 size={24} />,
    title: 'Government & Stakeholder Engagement',
    bullets: [
      'Direct coordination with federal authorities, municipal boards, and utilities',
      'Fast-tracking critical infrastructure clearances, zoning permissions, and approvals',
      'Strategic market-entry advisory and local regulatory compliance',
    ],
  },
  {
    icon: <Network size={24} />,
    title: 'Project Development Support',
    bullets: [
      'Technical feasibility studies, zoning analyses, and site due diligence',
      'Digital infrastructure master-planning and logistics alignment',
      'End-to-end B2B transaction facilitation and project governance',
    ],
  },
];

export default function DigitalInfrastructureTemplate({
  page,
  meta,
  slug,
}: {
  page?: any;
  meta?: any;
  slug?: string;
}) {
  const [animatedMW, setAnimatedMW] = useState(0);
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    // Staggered count animations for the track record metrics
    const duration = 2000;

    // 200 MW Animation
    const targetMW = 200;
    const stepMW = Math.max(Math.floor(duration / targetMW), 10);
    let currMW = 0;
    const timerMW = setInterval(() => {
      currMW += 2;
      if (currMW >= targetMW) {
        clearInterval(timerMW);
        setAnimatedMW(targetMW);
      } else {
        setAnimatedMW(currMW);
      }
    }, stepMW);

    // $6.8B Animation
    const targetVal = 6.8;
    const steps = 68;
    const stepVal = Math.max(Math.floor(duration / steps), 15);
    let currVal = 0;
    const timerVal = setInterval(() => {
      currVal += 0.1;
      if (currVal >= targetVal) {
        clearInterval(timerVal);
        setAnimatedValue(parseFloat(targetVal.toFixed(1)));
      } else {
        setAnimatedValue(parseFloat(currVal.toFixed(1)));
      }
    }, stepVal);

    return () => {
      clearInterval(timerMW);
      clearInterval(timerVal);
    };
  }, []);

  const pageTitle = page?.title || 'Data Centre & Digital Infrastructure';
  const subtitle = meta?.banner_subtitle || 'Powering Large-Scale Digital Investments';

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Jost, sans-serif' }}>
      <Header />

      {/* 1. Technical Dynamic Hero Banner */}
      <section className="relative pt-[140px] pb-24 overflow-hidden bg-gradient-to-br from-[#0c241a] via-[#006838] to-[#004d2a] text-white">
        {/* Futuristic Technical Background Grid Overlay */}
        <div className="absolute inset-0 z-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:30px_30px] opacity-60"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#000000]/30 z-0"></div>

        <div
          className="container mx-auto px-6 lg:px-12 relative z-10"
          style={{ maxWidth: '1480px' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column (Heading & Subtitle) - 60% */}
            <div className="lg:col-span-7 space-y-6">
              <div className="flex items-center gap-3 text-[#82BC41] text-xs font-black uppercase tracking-[0.25em]">
                <Cpu size={14} className="animate-pulse" />
                Enterprise Digital Infrastructure
              </div>
              <h1
                className="text-4xl md:text-6xl font-black uppercase tracking-tight leading-[0.95] text-white"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Data Centre <br />
                <span className="text-[#82BC41]">&amp; Digital Infrastructure</span>
              </h1>
              <p className="text-lg md:text-xl text-white/95 font-semibold leading-relaxed italic max-w-2xl">
                {subtitle}
              </p>
              <div className="w-16 h-1.5 bg-[#82BC41] rounded-none"></div>
              <p
                className="max-w-xl"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '15px',
                  fontWeight: 300,
                  lineHeight: '28.8px',
                  color: 'rgba(255, 255, 255, 0.85)',
                }}
              >
                At Plotae, we act as a specialized gateway and structuring partner to facilitate
                large-scale, high-density data centre and enterprise digital infrastructure
                investments across high-potential global markets.
              </p>
            </div>

            {/* Right Column (Glassmorphism Dashboard Mockup) - 40% */}
            <div className="lg:col-span-5">
              <div className="bg-white/5 border border-white/10 backdrop-blur-xl p-8 shadow-[0_30px_60px_rgba(0,0,0,0.25)] relative overflow-hidden group">
                <div className="absolute -top-12 -right-12 w-32 h-32 bg-[#82BC41]/10 rounded-full blur-2xl"></div>

                <div className="space-y-6 relative z-10">
                  <div className="flex justify-between items-center border-b border-white/10 pb-4">
                    <div>
                      <span className="text-[9px] font-black text-[#82BC41] tracking-widest block uppercase">
                        GLOBAL INVENTORY GATEWAY
                      </span>
                      <h4
                        className="text-sm font-black uppercase tracking-wider"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        Enterprise Capacity
                      </h4>
                    </div>
                    <span className="bg-[#82BC41]/20 text-[#82BC41] border border-[#82BC41]/30 text-[9px] font-black uppercase tracking-widest px-2.5 py-1">
                      Live Pipeline
                    </span>
                  </div>

                  {/* Core Capacity Statistics Block */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 p-4 border border-white/5 hover:border-white/10 transition-colors">
                      <span className="text-slate-400 text-[10px] font-black tracking-widest block uppercase">
                        ACTIVE SITES
                      </span>
                      <span
                        className="text-2xl font-black text-white"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        12+ Cities
                      </span>
                    </div>
                    <div className="bg-white/5 p-4 border border-white/5 hover:border-white/10 transition-colors">
                      <span className="text-slate-400 text-[10px] font-black tracking-widest block uppercase">
                        POWER CAP
                      </span>
                      <span
                        className="text-2xl font-black text-[#82BC41]"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        {animatedMW} MW+
                      </span>
                    </div>
                  </div>

                  {/* Quick Info bullet */}
                  <div className="flex gap-3 items-start text-xs text-white/80 leading-relaxed font-medium">
                    <Layers size={16} className="text-[#82BC41] mt-0.5 flex-shrink-0" />
                    <span>
                      Cross-border structuring, MW-level power allocation approvals, and direct
                      access to utility stakeholders.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. The Strategic Bridge callout banner */}
      <section className="py-16 bg-[#006838] text-white relative">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-3">
              <span className="text-[#82BC41] text-[10px] font-black uppercase tracking-[0.25em]">
                Strategic B2B Alliance
              </span>
              <p
                className="text-xl md:text-2xl font-black leading-tight uppercase"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                We act as a strategic bridge between investors, operators, landowners, and utility
                stakeholders.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <div className="border border-white/20 bg-white/5 px-6 py-4 flex items-center gap-4">
                <GitBranch className="text-[#82BC41] flex-shrink-0 animate-pulse" size={24} />
                <span className="text-xs font-semibold text-white/90">
                  Ensuring zero-friction project execution from concept to commissioning.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. High-Fidelity Service Matrix */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-[#006838] text-xs font-black uppercase tracking-[0.25em] block">
              Direct Solutions
            </span>
            <h2
              className="text-3xl md:text-4xl font-extrabold text-[#1a1a1a] uppercase"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Enterprise Infrastructure Services
            </h2>
            <div className="w-16 h-1 bg-[#82BC41] mx-auto rounded"></div>
            <p
              className="max-w-md mx-auto"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '15px',
                fontWeight: 300,
                lineHeight: '28.8px',
                color: 'rgb(80, 81, 79)',
              }}
            >
              Our B2B portfolio is engineered to address the distinct, complex lifecycle stages of
              digital infrastructure assets.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 md:p-10 border border-slate-100 shadow-[0_4px_25px_-5px_rgba(0,0,0,0.02)] hover:shadow-[0_15px_35px_rgba(0,0,0,0.06)] hover:border-slate-200 hover:-translate-y-1 transition-all duration-300 group flex flex-col justify-between"
              >
                <div className="space-y-6">
                  {/* Icon Block */}
                  <div className="w-12 h-12 bg-[#006838]/5 text-[#006838] group-hover:bg-[#006838] group-hover:text-white transition-colors duration-300 flex items-center justify-center rounded-none">
                    {service.icon}
                  </div>

                  {/* Service Title */}
                  <h3
                    className="text-lg md:text-xl font-black text-slate-800 uppercase tracking-wide"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {service.title}
                  </h3>

                  {/* Bullets List */}
                  <ul className="space-y-3 pt-2">
                    {service.bullets.map((bullet, bIdx) => (
                      <li key={bIdx} className="flex gap-3 items-start">
                        <span className="text-[#82BC41] font-black text-xs mt-0.5 flex-shrink-0">
                          🔹
                        </span>
                        <span className="text-slate-600 text-sm font-medium leading-relaxed">
                          {bullet}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border-t border-slate-100 pt-4 mt-8 flex justify-between items-center text-xs font-black">
                  <span className="text-slate-400">SERVICE CATEGORY</span>
                  <span className="text-[#006838] hover:text-[#82BC41] transition-colors flex items-center gap-1 cursor-pointer group">
                    Active Delivery
                    <ArrowUpRight
                      size={14}
                      className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Experience & Track Record Stats section */}
      <section className="py-24 bg-slate-50 border-y border-slate-100">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Intro Text (40%) */}
            <div className="lg:col-span-5 space-y-6">
              <span className="text-[#006838] text-xs font-black uppercase tracking-[0.25em] block">
                Demonstrated Capability
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#1a1a1a] uppercase leading-tight"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Track Record &amp; Large-Scale Impact
              </h2>
              <div className="w-12 h-1 bg-[#82BC41] rounded"></div>

              <p
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '15px',
                  fontWeight: 300,
                  lineHeight: '28.8px',
                  color: 'rgb(80, 81, 79)',
                }}
              >
                Our active operations and facilitation pipelines cover critical, high-density
                infrastructure sectors globally. We are proud to demonstrate major impact across
                strategic developing nodes.
              </p>

              <div className="bg-white p-6 border-l-4 border-[#006838] shadow-sm text-xs font-semibold leading-relaxed text-slate-500">
                Our expertise spans comprehensive deal structuring, utility interconnections,
                physical site security auditing, and localized government compliance.
              </div>
            </div>

            {/* Right Large Stats Cards (60%) */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-8">
              {/* Card 1 ($6.8B) */}
              <div className="bg-white p-8 border border-slate-150 shadow-[0_4px_25px_-5px_rgba(0,0,0,0.02)] hover:shadow-[0_15px_30px_rgba(0,0,0,0.05)] hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between group">
                <div className="space-y-4">
                  <span className="text-[#006838] text-[10px] font-black uppercase tracking-widest block bg-[#006838]/5 w-fit px-2.5 py-1">
                    AI-READY DEVELOPMENT
                  </span>
                  <div
                    className="text-4xl md:text-5xl font-black text-slate-800 tracking-tighter"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    ${animatedValue}B
                    <span className="text-lg font-black text-[#82BC41] ml-0.5">USD</span>
                  </div>
                  <h4 className="text-sm font-extrabold text-slate-700 uppercase tracking-wide">
                    Tier III Data Centre Enablement
                  </h4>
                  <p className="text-slate-500 text-xs leading-relaxed font-medium">
                    Participated in structuring AI-focused data centre infrastructure in India with
                    an estimated capacity of <strong>200 MW</strong>.
                  </p>
                </div>
                <div className="border-t border-slate-100 pt-4 mt-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  Completed Phase I
                </div>
              </div>

              {/* Card 2 ($10 Billion) */}
              <div className="bg-white p-8 border border-slate-150 shadow-[0_4px_25px_-5px_rgba(0,0,0,0.02)] hover:shadow-[0_15px_30px_rgba(0,0,0,0.05)] hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between group">
                <div className="space-y-4">
                  <span className="text-[#006838] text-[10px] font-black uppercase tracking-widest block bg-[#006838]/5 w-fit px-2.5 py-1">
                    ACTIVE PIPELINE
                  </span>
                  <div
                    className="text-4xl md:text-5xl font-black text-[#006838] tracking-tighter"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    $10 Billion
                  </div>
                  <h4 className="text-sm font-extrabold text-slate-700 uppercase tracking-wide">
                    Pipeline Project
                  </h4>
                  <p
                    style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '15px',
                      fontWeight: 300,
                      lineHeight: '28.8px',
                      color: 'rgb(80, 81, 79)',
                    }}
                  >
                    Currently engaged in a pipeline data centre project in India with an estimated
                    value of <strong>USD 1 billion</strong> and planned capacity of approximately{' '}
                    <strong>25 MW</strong>.
                  </p>
                </div>
                <div className="border-t border-slate-100 pt-4 mt-6 text-[10px] font-black text-[#82BC41] uppercase tracking-widest">
                  Under Execution
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Geographic Network Presence */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Sticky Column (33%) */}
            <div className="lg:col-span-4 lg:sticky lg:top-28 h-fit space-y-4">
              <span className="text-[#006838] text-xs font-black uppercase tracking-[0.25em] block">
                Geographical Nodes
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#1a1a1a] uppercase leading-tight"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Our Global Footprint
              </h2>
              <div className="w-12 h-1 bg-[#82BC41] rounded"></div>
              <p
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '15px',
                  fontWeight: 300,
                  lineHeight: '28.8px',
                  color: 'rgb(80, 81, 79)',
                }}
              >
                Bizmaker and Plotae coordinate infrastructure projects across highly active
                industrial and technology zones globally.
              </p>

              <div className="pt-2">
                <div className="inline-flex items-center gap-2 bg-[#006838]/5 text-[#006838] text-xs font-bold px-4 py-2 uppercase tracking-wide">
                  <Globe size={14} className="animate-spin-slow" />
                  Multilateral Delivery Network
                </div>
              </div>
            </div>

            {/* Right Presence Directories (66%) */}
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {/* Node 1: India */}
              <div className="bg-slate-50 p-6 border border-slate-100 flex flex-col justify-between">
                <div className="space-y-4">
                  <span className="text-[10px] font-black text-[#006838] uppercase tracking-widest block">
                    SOUTH ASIA
                  </span>
                  <h4
                    className="text-xl font-black text-slate-800 uppercase"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    India
                  </h4>
                  <div className="w-6 h-0.5 bg-[#82BC41]"></div>

                  <ul className="space-y-2 text-slate-500 text-xs font-bold tracking-wide">
                    <li>• NAVI MUMBAI</li>
                    <li>• CHENNAI</li>
                    <li>• HYDERABAD</li>
                    <li>• GREATER NOIDA</li>
                    <li>• PUNE</li>
                    <li>• BANGALORE</li>
                  </ul>
                </div>
              </div>

              {/* Node 2: Middle East */}
              <div className="bg-slate-50 p-6 border border-slate-100 flex flex-col justify-between">
                <div className="space-y-4">
                  <span className="text-[10px] font-black text-[#006838] uppercase tracking-widest block">
                    MIDDLE EAST
                  </span>
                  <h4
                    className="text-xl font-black text-slate-800 uppercase"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    GCC Region
                  </h4>
                  <div className="w-6 h-0.5 bg-[#006838]"></div>

                  <ul className="space-y-2 text-slate-500 text-xs font-bold tracking-wide">
                    <li>• UNITED ARAB EMIRATES (UAE)</li>
                    <li>• SAUDI ARABIA (KSA)</li>
                  </ul>
                </div>
              </div>

              {/* Node 3: International */}
              <div className="bg-slate-50 p-6 border border-slate-100 flex flex-col justify-between">
                <div className="space-y-4">
                  <span className="text-[10px] font-black text-[#006838] uppercase tracking-widest block">
                    GLOBAL MARKETS
                  </span>
                  <h4
                    className="text-xl font-black text-slate-800 uppercase"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    International
                  </h4>
                  <div className="w-6 h-0.5 bg-[#82BC41]"></div>

                  <ul className="space-y-2 text-slate-500 text-xs font-bold tracking-wide">
                    <li>• SOUTHEAST ASIA</li>
                    <li>• UNITED KINGDOM (UK)</li>
                    <li>• UNITED STATES (USA)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Why Plotae checklist */}
      <section className="py-24 bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left column bullet list (60%) */}
            <div className="lg:col-span-6 bg-white p-8 border border-slate-150 shadow-sm order-2 lg:order-1">
              <h5 className="text-[11px] font-black text-[#006838] uppercase tracking-widest mb-6">
                Our Strategic Advantages:
              </h5>

              <ul className="space-y-5">
                {[
                  {
                    title: 'Access & Coordination Authority',
                    desc: 'Unprecedented, direct access to municipal authorities, grid developers, and government utilities.',
                  },
                  {
                    title: 'Billions in Facilitated Projects',
                    desc: 'Demonstrated transaction record in multi-billion dollar strategic asset structuring.',
                  },
                  {
                    title: 'Vast Institutional Network',
                    desc: 'Direct interfaces connecting land bank owners, international operators, and capital funds.',
                  },
                  {
                    title: 'Comprehensive Compliance Advisory',
                    desc: 'Pre-vetted deal structuring, corporate governance compliance, and local deployment blueprints.',
                  },
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-4 items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-[#006838]/10 text-[#006838] rounded-full flex items-center justify-center mt-0.5">
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                    <div>
                      <strong className="text-slate-800 font-extrabold text-sm uppercase block tracking-wide">
                        {item.title}
                      </strong>
                      <span className="text-slate-600 text-xs leading-relaxed font-medium">
                        {item.desc}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right column description (40%) */}
            <div className="lg:col-span-6 space-y-6 order-1 lg:order-2">
              <span className="text-[#006838] text-xs font-black uppercase tracking-[0.25em] block">
                ⚡ WHY PLOTAE
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#1a1a1a] uppercase leading-tight"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Your Gateway to Digital Growth
              </h2>
              <div className="w-12 h-1 bg-[#82BC41] rounded"></div>
              <p
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '15px',
                  fontWeight: 300,
                  lineHeight: '28.8px',
                  color: 'rgb(80, 81, 79)',
                }}
              >
                Digital infrastructure requires absolute synergy between raw physical land, grid
                power availability, hyper-redundant connectivity, and strategic capital structuring.
              </p>
              <p
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '15px',
                  fontWeight: 300,
                  lineHeight: '28.8px',
                  color: 'rgb(80, 81, 79)',
                  marginTop: '16px',
                }}
              >
                At Plotae, we align these elements perfectly to enable seamless development cycles
                for global operators and institutional real estate developers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Confidentiality compliance module */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1000px' }}>
          <div className="border-4 border-double border-[#006838]/20 bg-slate-50 p-8 md:p-10 flex flex-col sm:flex-row items-start gap-6">
            <div className="w-14 h-14 bg-[#006838]/10 text-[#006838] flex items-center justify-center flex-shrink-0">
              <Lock size={26} />
            </div>

            <div className="space-y-3">
              <h4
                className="text-sm font-black text-slate-800 uppercase tracking-wide"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Confidentiality Note &amp; Compliance Statement
              </h4>
              <p
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '15px',
                  fontWeight: 300,
                  lineHeight: '28.8px',
                  color: 'rgb(80, 81, 79)',
                }}
              >
                Due to the highly sensitive and strategic nature of national digital infrastructure
                deployments, detailed site data packets, network diagrams, utility approval files,
                and pricing sheets are maintained strictly confidential. Supporting documents can be
                shared with qualified principals upon request and verification under NDA.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Call To Action section */}
      <section className="py-24 bg-gradient-to-br from-[#0c241a] via-[#006838] to-[#004d2a] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.05),transparent)]"></div>
        <div className="container mx-auto px-6 text-center max-w-3xl relative z-10 space-y-6">
          <span className="text-[#82BC41] text-xs font-black uppercase tracking-[0.3em] block">
            Coordinated Execution
          </span>
          <h2
            className="text-3xl md:text-4xl font-black uppercase leading-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Partner With Us
          </h2>
          <p
            className="max-w-lg mx-auto"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '15px',
              fontWeight: 300,
              lineHeight: '28.8px',
              color: 'rgba(255, 255, 255, 0.85)',
            }}
          >
            Whether you are an institutional investor, operator, landowner, or utility provider,
            Plotae is your trusted gateway in digital infrastructure development.
          </p>

          <div className="pt-4 flex justify-center items-center">
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 bg-[#82BC41] hover:bg-white text-slate-900 hover:text-[#006838] font-black text-xs uppercase tracking-widest px-10 py-5 transition-all duration-300 rounded-none shadow-lg group hover:scale-[1.02]"
            >
              Discuss Your Project
              <ChevronRight
                size={14}
                className="transition-transform duration-300 group-hover:translate-x-0.5"
              />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
